import { formatTime, asDate } from './formatters';
import { normalizeStage } from './status';
import { getDelayAttribution } from './delayAttribution';

function lineStatus(item) {
  const raw = String(item?.status || item?.itemStatus || '').trim();
  return raw || 'Pending';
}

function resolveLineTotal(item) {
  const explicit = item?.lineTotal ?? item?.price ?? item?.total;
  if (explicit != null && explicit !== '') return Number(explicit || 0);

  const unitPrice = Number(item?.unitPrice || 0);
  const qty = Number(item?.qty || item?.quantity || 1);
  return Number(unitPrice * qty || 0);
}

export function normalizeOrder(docLike) {
  const raw = docLike || {};
  const id = raw.id || raw.orderId || raw.orderNumber || `OT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  const customer = raw.customer || {};
  const assignment = raw.assignment || {};
  const pricing = raw.pricing || {};
  const payment = raw.payment || {};
  const notes = raw.notes || {};
  const createdAt = asDate(raw.placedAt || raw.createdAt || raw.updatedAt || new Date());
  const items = Array.isArray(raw.items) ? raw.items : [];
  const itemsNormalized = items.map((item, index) => ({
    lineId: item.lineId || `${id}-${index}`,
    name: item.name || item.title || item.sku || `Item ${index + 1}`,
    qty: Number(item.qty || item.quantity || 1),
    status: lineStatus(item),
    lineTotal: resolveLineTotal(item),
    unitPrice: Number(item.unitPrice || 0),
  }));

  const normalized = {
    raw,
    id,
    orderNumber: raw.orderNumber || id,
    customerName: customer.name || raw.customerName || 'Customer',
    customerPhone: customer.phone || raw.customerPhone || '',
    suburb: customer.suburb || raw.suburb || raw.area || 'Unassigned area',
    address: [customer.addressLine1 || raw.address, customer.addressLine2, customer.suburb].filter(Boolean).join(', '),
    notes: notes.online || notes.customerVisible || raw.notes || '',
    paymentMethod: payment.label || payment.method || raw.paymentMethod || 'Unknown',
    sentByName: raw.sentByName || raw.senderName || raw.createdByName || raw.createdBy?.name || raw.createdBy?.displayName || '',
    sentByRoleLabel: raw.sentByRoleLabel || raw.senderRoleLabel || raw.createdByRoleLabel || raw.createdBy?.roleLabel || '',
    senderName: raw.senderName || raw.sentByName || '',
    senderRoleLabel: raw.senderRoleLabel || raw.sentByRoleLabel || '',
    createdByName: raw.createdByName || raw.createdBy?.name || raw.createdBy?.displayName || '',
    createdByDisplayName: raw.createdByDisplayName || raw.createdBy?.displayName || raw.createdBy?.name || '',
    createdByRoleLabel: raw.createdByRoleLabel || raw.createdBy?.roleLabel || '',
    createdBy: raw.createdBy || null,
    total: Number(pricing.total ?? raw.total ?? 0),
    subtotal: Number(pricing.subtotal ?? raw.subtotal ?? itemsNormalized.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0)),
    deliveryFee: Number(pricing.deliveryFee ?? raw.deliveryFee ?? 0),
    source: raw.source || 'Website',
    stage: normalizeStage(raw.stage || raw.status || raw.subStatus),
    store: raw.storeName || raw.store || raw.storeId || 'Unassigned store',
    assignedOnline: raw.assignedOnline || assignment.onlineName || assignment.onlineUserId || raw.onlineOwner || raw.createdBy?.name || '',
    assignedDriver: raw.assignedDriver || assignment.driverName || assignment.driverId || '',
    assignedStoreUser: raw.assignedStoreUser || assignment.storeUserId || '',
    timePlaced: formatTime(createdAt),
    eta: raw.eta || raw.etaText || '—',
    items: itemsNormalized,
    createdAt,
    updatedAt: asDate(raw.updatedAt),
    placedAt: createdAt,
    timeline: Array.isArray(raw.timeline) ? raw.timeline : [],
    issueFlag: Boolean(raw.flags?.hasStockIssue || raw.flags?.requiresCustomerContact || raw.isIssue || raw.issueFlag),
    paymentStatus: payment.status || raw.paymentStatus || '',
    driverLoad: Number(raw.driverLoad || 0),
    storeStatus: raw.storeStatus || raw.fulfilment?.storeStatus || '',
    fulfilment: raw.fulfilment || {},
    timings: raw.timings || {},
    flags: raw.flags || {},
    issueNote: raw.issueNote || raw.notes?.online || raw.notes?.store || '',
    stageHistory: raw.stageHistory || {},
    currentOwner: raw.currentOwner || '',
    lastEventAt: raw.lastEventAt || null,
    lastEventType: raw.lastEventType || '',
    events: Array.isArray(raw.events) ? raw.events : [],
    storeProof: raw.storeProof || null,
    proof: raw.proof || null,
    swapApproved: Boolean(raw.swapApproved),
    additions: Array.isArray(raw.additions) ? raw.additions : [],
    originalOrderItems: Array.isArray(raw.originalOrderItems) ? raw.originalOrderItems : [],
    unavailableItems: Array.isArray(raw.unavailableItems) ? raw.unavailableItems : [],
    unavailableItemNames: Array.isArray(raw.unavailableItemNames) ? raw.unavailableItemNames : [],
    packerName: raw.packerName || raw.storeProof?.packedBy || raw.proof?.packedBy || '',
    assignedDriverName: raw.assignedDriverName || raw.assignment?.driverName || '',
    linkChatMessages: Array.isArray(raw.linkChatMessages) ? raw.linkChatMessages : [],
  };

  normalized.delay = getDelayAttribution(normalized);
  return normalized;
}

